import apiClient from './apiClient';
import endpoints from './endpoints';

export type TaskStatus = 'not_started' | 'in_progress' | 'completed';

export interface OnboardingTaskPayload {
  taskId: string;
  status: TaskStatus;
  sortId: number;
}

export interface OnboardingTrackingRecord {
  id: number;
  userId: number;
  tenantId: number;
  groupTasksId: string;
  status: string;
  totalTasks: number;
  taskCompleted: number;
  startedAt: string;
  completedAt: string | null;
  tasks: OnboardingTaskPayload[];
}

export async function getOnboardingTracking(
  groupTasksId: string
): Promise<OnboardingTrackingRecord[]> {
  const res = await apiClient.get(
    `${endpoints.ONBOARDING.GET_TRACKING}?groupTasksId=${groupTasksId}`
  );
  const raw = res.data?.data ?? res.data ?? [];

  // Already a proper array
  if (Array.isArray(raw)) return raw;

  // null / non-object
  if (!raw || typeof raw !== 'object') return [];

  const keys = Object.keys(raw);

  // Empty object {} — no records on server
  if (keys.length === 0) return [];

  // Numeric-keyed object {"0": {...}, "1": {...}} — API serialised array as object
  if (keys.every((k) => !isNaN(Number(k)))) {
    return Object.values(raw) as OnboardingTrackingRecord[];
  }

  // Plain single record object {id, tasks, ...}
  return [raw as OnboardingTrackingRecord];
}

export async function createOnboardingTracking(payload: {
  groupTasksId: string;
  tasks: OnboardingTaskPayload[];
}): Promise<OnboardingTrackingRecord> {
  const res = await apiClient.post(endpoints.ONBOARDING.CREATE_TRACKING, payload);
  return res.data?.data ?? res.data;
}

export async function updateOnboardingTracking(
  id: number,
  tasks: OnboardingTaskPayload[]
): Promise<OnboardingTrackingRecord> {
  const res = await apiClient.put(
    `${endpoints.ONBOARDING.UPDATE_TRACKING}/${id}`,
    { tasks }
  );
  return res.data?.data ?? res.data;
}
